/**
 * The version in force, or `null` when this block has none.
 *
 * Resolved by ID, never by index — §4.2 of the design. A teacher can delete a version while a
 * preview run is open, and the drawn id is held in the session roll; indices renumber underneath
 * that, ids do not. An id that no longer exists falls back to the first version rather than
 * throwing, or worse, silently selecting whatever slid into the vacated slot.
 *
 * An absent `versionId` also lands on the first version, which is what the editor wants before a
 * draw exists.
 */
export function pickVersion(versions, versionId) {
    if (!versions || versions.length === 0)
        return null;
    return versions.find(v => v.id === versionId) ?? versions[0];
}
/**
 * Everything a version replaces that does not depend on the shape of its answer key.
 *
 * Separate from the key because the six choice blocks do not agree on what their answer space is
 * even called — `options` on mcq/dropdown/keyword, `values` on scale, `blanks` on fitb, `items` on
 * ordering — and the value blocks have no roles at all. A single generic pretending otherwise
 * would be a generic in name only. Each block composes this with the one line that re-keys its
 * own collection.
 *
 * Clearing `conditionalEquation`/`conditionalBranches` when the version carries none is
 * deliberate: once versions exist the version owns the answer setup completely, so block-level
 * branches authored before it must not keep firing underneath.
 */
export function applyVersionHead(data, version) {
    return {
        ...data,
        question: version.question,
        feedback: version.feedback,
        conditionalEquation: version.conditionalEquation,
        conditionalBranches: version.conditionalBranches,
        conditionalBranchesDraft: version.conditionalBranchesDraft,
    };
}
/**
 * Re-key a collection of `{ id, role }` against a version's role map — options, scale values, or
 * anything else identified the same way.
 *
 * An item the version says nothing about is returned AS THE SAME OBJECT, keeping its own role. A
 * version authored before an option was added therefore leaves that option alone rather than
 * silently neutralising it, which is the fail-closed direction for an answer key.
 */
export function versionRoles(items, roles) {
    return versionRolesBy(items, roles, item => item.id);
}
/**
 * The same merge, for a block whose answer key is not keyed by `id`.
 *
 * Scale's roles are keyed by `buildScaleValueTrigger(value)` because a Scale point has a position
 * rather than an id; FITB's are keyed per (blank, word). The merge SEMANTICS are identical in
 * every case — an item the version does not mention keeps its own role and its own identity — so
 * only the key differs, and that is the one thing a block should have to say.
 */
export function versionRolesBy(items, roles, keyOf) {
    return items.map(item => {
        const role = roles[keyOf(item)];
        return role ? { ...item, role } : item;
    });
}
/**
 * Put the drawn version in force on a block whose answer space is `options` — mcq, dropdown,
 * keyword. The other families compose `applyVersionHead` with their own collection themselves.
 *
 * ## Identity when there are no versions
 *
 * Returns the SAME OBJECT, not an equal copy — `applyOptionsVersion(d, x) === d` holds for every
 * block that has never had a version authored, which is every block in the database today. That
 * is what makes "this change cannot alter existing behaviour" a fact the suite proves rather than
 * a claim, and it keeps React identity checks working for callers memoising on normalised data.
 */
export function applyOptionsVersion(data, versionId) {
    const version = pickVersion(data.versions, versionId);
    if (!version)
        return data;
    return { ...applyVersionHead(data, version), options: versionRoles(data.options, version.roles) };
}
/** Whether this block is a bank. One version is not a bank — it is a block with a stray array. */
export function hasVersions(data) {
    return !!data.versions && data.versions.length > 1;
}
/**
 * How many versions to SHOW, which is zero unless the block is actually a bank.
 *
 * Deliberately not `data.versions?.length`. A block mid-migration holds exactly one version and is
 * not a bank, so every caller would have to pair the length with `hasVersions` and one of them
 * eventually would not — and a card reading "×1" is worse than no badge, because it advertises a
 * choice that does not exist. The two facts are one fact, so they are one function.
 */
export function versionCount(data) {
    return hasVersions(data) ? data.versions.length : 0;
}
/**
 * The write half of `applyOptionsVersion` — split an edit back into the version it belongs to.
 *
 * The editor renders from version-resolved data, so what it hands back is a whole block with the
 * version's question, feedback and roles already merged in. This puts each part where it lives:
 * the question, feedback, roles and branches into the version being edited; everything else — the
 * options' identity and text, the count, the grading mode — onto the block, because those are the
 * answer SPACE and are shared.
 *
 * Identity when there are no versions, exactly as the read side is: `next` straight back.
 *
 * The block's own `question`/`feedback`/roles are deliberately left untouched once versions exist.
 * They are vestigial from that point — frozen at whatever they held when the first version was
 * created — and writing the edited copy into them too would make a block that looks correct in
 * two places and can disagree with itself.
 */
export function writeOptionsVersion(current, versionId, next) {
    return writeVersionWithRoles(current, versionId, next, Object.fromEntries(next.options.map(o => [o.id, o.role])));
}
/**
 * The write half, with the answer key supplied by the caller.
 *
 * Splitting the ROLES out is the whole difference between blocks: MCQ keys them by option id,
 * Scale by `buildScaleValueTrigger(value)` because a point has a position rather than an
 * identity, FITB per (blank, word). What stays identical — which fields belong to the version and
 * which stay on the block as the shared answer SPACE — is everything else, so it lives here once.
 */
export function writeVersionWithRoles(current, versionId, next, roles) {
    return writeVersionWith(current, versionId, next, { roles });
}
/**
 * The write half, with the ANSWER KEY supplied by the caller whatever its shape.
 *
 * What belongs to the version and what stays on the block as the shared answer SPACE is identical
 * for every block, so it lives here; the key itself is a payload because a choice block's is a
 * roles map, Ordering's is an id sequence and a value block's is `answers[]` with an error model.
 * See §3.2 of `docs/question-versions.md` for the table.
 */
/**
 * EDITING A VERSION IS THE DECISION ITS `unreviewed` MARK WAS WAITING FOR.
 *
 * A projected version starts as a copy of another one (`syncDrivenVersions`), which is the right
 * convenience and the familiar trap: nothing on screen separates a considered question from a
 * duplicated one, so a teacher who customises three of five leaves two that look finished and
 * ask the wrong thing. The mark is what the issue list reads to say so, and this is what takes
 * it off.
 *
 * Called from BOTH writers below — `writeVersionWith` and `writeFieldVersion` — because the
 * twelve blocks funnel through one or the other and neither is reducible to the other. Stated
 * once here rather than twice there, which is also where a third writer would have to look.
 *
 * The scenario sibling is `markScenarioReviewed`, which takes a list and an id because a block
 * clears one row explicitly; a version is cleared by the write that edits it, so this takes the
 * version.
 */
function markVersionReviewed(version) {
    /* Assigned rather than deleted, which is `applyVersionHead`'s convention and the reason a
       spread carries a clear through. No guard on `unreviewed` being set: both callers spread
       the result immediately, so returning the original unchanged would save nothing. */
    return { ...version, unreviewed: undefined };
}
export function writeVersionWith(current, versionId, next, payload) {
    const version = pickVersion(current.versions, versionId);
    /* NO BANK IS STILL A WRITE, and a caller spreading a stale copy of its own data is still
       carrying whatever link that copy was seeded with — see `restateLinkFields`. This path used
       to return the payload untouched, which is precisely the state dropping a driven axis
       leaves the block in: no versions, and a local copy still holding the old address. */
    if (!version)
        return restateLinkFields(current, next);
    const edited = {
        ...markVersionReviewed(version),
        question: next.question,
        feedback: next.feedback,
        ...payload,
        conditionalEquation: next.conditionalEquation,
        conditionalBranches: next.conditionalBranches,
        conditionalBranchesDraft: next.conditionalBranchesDraft,
    };
    return {
        ...next,
        question: current.question,
        feedback: current.feedback,
        conditionalEquation: current.conditionalEquation,
        conditionalBranches: current.conditionalBranches,
        conditionalBranchesDraft: current.conditionalBranchesDraft,
        /*
         * WHAT THE AXIS OWNS COMES FROM `current`, NEVER FROM THE PAYLOAD — like `versions` below,
         * and for a reason that has now cost a real lesson.
         *
         * A FIELD WRITE CANNOT CHANGE WHAT A BLOCK VARIES WITH. The link is made and broken by the
         * link menu, which writes the whole block; nothing that edits a question or an answer key
         * has any business saying who the leader or the driver is. Restating them here makes that
         * true by construction instead of depending on every caller to leave them out.
         *
         * Reported: "i started typing the question and the link was immediately dropped with a
         * note that says 'link lost' ... versions, left behind." Numerical is the one block that
         * keeps a FULL local copy of its data while editing, and `setField` emits
         * `{ ...fields, feedback }` — the whole copy. Seeded at mount, so a link made with the
         * editor already open never reached it; and because `initData` names `drivenBy` on every
         * branch, the copy carried it as an OWN KEY holding `undefined`. `{ ...base, ...next }`
         * then wrote that `undefined` over the real address on the first keystroke.
         *
         * Every consequence followed from the one lost field: the versions kept their row ids, so
         * `versionAxisOwner` read `orphaned`, the trigger said "Link lost", and the row box
         * unmounted under the cursor — which is the lost focus.
         *
         * Measured in the owner's own data: `Numerical 2`, four versions still holding
         * `default-option-1` / `default-option-2` / a uuid / `__blank`, no `drivenBy`, and version
         * one's question reading `<p>wHoa</p>`.
         *
         * Fixed HERE rather than in Numerical's `emitFields` because the hazard is not Numerical's:
         * it is any caller that spreads an object wider than the fields it changed, and this is the
         * funnel every block's field write already passes through.
         */
        ...pick(current, LINK_FIELDS),
        versions: (current.versions ?? []).map(v => (v.id === version.id ? edited : v)),
    };
}
/** The named keys that `from` actually has — see `restateLinkFields` for why absence matters. */
function pick(from, keys) {
    const rec = from;
    return Object.fromEntries(keys.filter(k => k in rec).map(k => [k, rec[k]]));
}
/** The three fields that say what a block VARIES WITH. */
const LINK_FIELDS = ['followsBlockId', 'drivenBy', 'scenarioBranches'];
/**
 * WHAT A FIELD WRITE MAY NEVER CHANGE: which axis owns the block, and its bank.
 *
 * `versions` belongs here on the no-bank path for the same reason the link does, and leaving
 * it out turned one report into the next. Dropping a driven axis clears the link AND the bank
 * together; restating only the link left the stale copy's versions to come back on the next
 * write, with no `drivenBy` above them — which is the `orphaned` state. Reported as "i
 * disconnected a block from driven by and when i come back, it's reconnected again but just
 * says 'link lost'".
 *
 * Safe because `pickVersion` returns null ONLY when there are no versions at all: this path
 * is reached by an unbanked block, so restating `versions` says "stay unbanked". The banked
 * path writes `out.versions` itself and never consults this list.
 *
 * `scenarioBranches` is deliberately NOT here. On a block with no bank it is the PAYLOAD: a
 * follower has no versions by construction, so every row write arrives down the same path,
 * and restating it from the stored block wrote each edit straight back out. Thirty-five
 * scenario tests said so at once.
 */
const AXIS_LINK_FIELDS = ['followsBlockId', 'drivenBy', 'versions'];
/**
 * FORCE A WRITE'S AXIS LINK TO AGREE WITH THE STORED BLOCK.
 *
 * Loosely typed on purpose: `VersionHeadFields` does not declare these, because a value block
 * and a choice block disagree about almost everything else and they ride on the block data
 * rather than on the version.
 *
 * A present key is copied and an ABSENT one is deleted, which is the half the banked path's
 * restate never needed and this one does. A caller spreading a stale copy of its own data
 * carries whatever link that copy was seeded with, and dropping a link is exactly the case
 * where the stored block has let the key go. Reported as "if i set to none and then close the
 * block, when i return it's reconnected to the same block again".
 *
 * Deleting rather than assigning `undefined` is deliberate: the undo ring reads the diff
 * between what was emitted and what was stored, and a block that has never been linked must
 * not gain keys it never had.
 */
export function restateLinkFields(current, next) {
    const from = current;
    const out = { ...next };
    let touched = false;
    for (const key of AXIS_LINK_FIELDS) {
        if (key in from) {
            if (out[key] !== from[key]) {
                out[key] = from[key];
                touched = true;
            }
        }
        else if (key in out) {
            delete out[key];
            touched = true;
        }
    }
    return touched ? out : next;
}
/**
 * Turn a block into a bank, or add another variant to one — what "+ new version" does.
 *
 * The first call migrates what the block already holds into version 1 and then duplicates it, so
 * a teacher always lands on two versions to compare rather than one and an empty slot. Later
 * calls duplicate whichever version is in force, which is the one on screen.
 *
 * **Branches are deep-copied with FRESH IDS**, and that is not tidiness. Flow keys a
 * branch-collapsed condition on the stable id set — `[...scope.branchIds].sort().join('+')` in
 * `getConditionSignature` — so reusing an id across versions makes a condition contributing in
 * (v1,b1) and (v2,b1) indistinguishable from one contributing only in (v1,b1), and drift
 * detection quietly stops noticing real changes.
 */
export function addVersion(data, fromVersionId, newId) {
    return addVersionWithRoles(data, fromVersionId, newId, Object.fromEntries(data.options.map(o => [o.id, o.role])));
}
/**
 * The same, for a block whose answer key is not keyed by option id.
 *
 * `rolesNow` is only consulted when the block has no bank yet and is being migrated into one — the
 * new v1 has to carry the roles the block is holding right now, or creating a bank would silently
 * neutralise the answer key. Every later version duplicates an existing one and never needs it.
 */
export function addVersionWithRoles(data, fromVersionId, newId, rolesNow) {
    return addVersionWith(data, fromVersionId, newId, { roles: rolesNow });
}
/**
 * The same, with the block's own answer key as the migration snapshot.
 *
 * `keyNow` is only consulted when the block has no bank yet and is being migrated into one — the
 * new v1 has to carry the answer the block is holding right now, or creating a bank would
 * silently blank it. Every later version duplicates an existing one and never needs it.
 */
export function addVersionWith(data, fromVersionId, newId, keyNow) {
    const base = pickVersion(data.versions, fromVersionId) ?? {
        id: newId(),
        question: data.question,
        feedback: data.feedback,
        ...keyNow,
        conditionalEquation: data.conditionalEquation,
        conditionalBranches: data.conditionalBranches,
        conditionalBranchesDraft: data.conditionalBranchesDraft,
    };
    const copy = {
        ...base,
        id: newId(),
        conditionalBranches: base.conditionalBranches?.map(b => ({ ...b, id: newId() })),
        // Fresh ids here too: a draft is a set of branches waiting to be switched back on, and two
        // versions holding the same branch id is the drift-detection hazard either way.
        conditionalBranchesDraft: base.conditionalBranchesDraft
            ? { ...base.conditionalBranchesDraft, branches: base.conditionalBranchesDraft.branches.map(b => ({ ...b, id: newId() })) }
            : undefined,
    };
    return { ...data, versions: [...(data.versions ?? [base]), copy] };
}
/**
 * Remove a version — and collapse back to a plain block when only one would be left.
 *
 * The collapse is the point. A block holding a single version is a block with a stray array: it
 * reads as a bank in the data and behaves as an ordinary question, and `hasVersions` already has
 * to say so. So deleting the second-to-last version promotes the survivor back onto the block and
 * clears `versions` entirely, which also makes the whole feature REVERSIBLE — a teacher who tries
 * a bank and does not want one can get back to exactly the block they had.
 *
 * Removing the only version, or one that is not there, is a no-op rather than an error. Nothing
 * downstream benefits from distinguishing "you cannot" from "there was nothing to do", and the
 * caller would only have to re-check what this already knows.
 */
export function removeVersion(data, versionId) {
    return removeVersionWith(data, versionId, applyOptionsVersion);
}
/**
 * The same, for a block that resolves a version its own way.
 *
 * Collapsing the last two back to a plain block means folding the survivor's content AND its
 * answer key into the block itself — which is exactly what that block's own `apply` already does,
 * so it is passed in rather than reimplemented per block.
 */
export function removeVersionWith(data, versionId, applyVersion) {
    const versions = data.versions ?? [];
    if (versions.length <= 1 || !versions.some(v => v.id === versionId))
        return data;
    const left = versions.filter(v => v.id !== versionId);
    if (left.length > 1)
        return { ...data, versions: left };
    return { ...applyVersion(data, left[0].id), versions: undefined };
}
/**
 * Where a block's drawn version lives inside the task's roll.
 *
 * In the ROLL rather than beside it, so the whole lifecycle comes for free: `roll()` is already
 * "produced on first ask and sticky for the run", and `start(experienceId, seedRolls?)` already
 * separates the two ways of starting over. One draw per task per session, a new one when the
 * student restarts, a new one on Reroll (no seed), the SAME one on Reset (seeded) — none of which
 * needs a second lifecycle kept in step with the first. `SessionRecord.rolls` is recorded too, so
 * which version was asked stays answerable later without a new field on the attempt.
 *
 * `@` can never collide with a variable: a name must match `/^[a-zA-Z][a-zA-Z0-9_]*$/`, so it has
 * to begin with a letter, and `:` is not legal anywhere in one either.
 */
export function versionRollKey(blockId) {
    return `@version:${blockId}`;
}
/**
 * Draw a version for every block in a task that has a bank.
 *
 * PER BLOCK, and blocks are deliberately NOT locked to one another. Two 2-version blocks in a
 * task therefore produce four combinations as a PRODUCT, which is exactly the four screens the
 * exam this feature came from had to enumerate by hand.
 *
 * Blocks with no bank contribute nothing, so the roll stays empty for the overwhelming majority
 * of tasks and `pickVersion`'s own fallback covers a block whose draw is missing.
 *
 * A DRIVEN BANK IS NOT DRAWN AT ALL. Its version is selected by the student's own answer to an
 * earlier block, which does not exist when the task is rolled — and a roll entry would be worse
 * than useless, because `pickVersion` reads the roll and would hand back a randomly chosen
 * version for the whole run while the selector sat underneath it with the right answer. One
 * axis, one owner: a bank is drawn or it is driven, never both.
 */
export function rollVersions(blocks, pick = n => Math.floor(Math.random() * n)) {
    const out = {};
    for (const block of blocks) {
        const versions = block.data?.versions;
        if (!versions || versions.length < 2 || block.data?.drivenBy)
            continue;
        out[versionRollKey(block.id)] = versions[pick(versions.length)].id;
    }
    return out;
}
/**
 * The branch sets a block must satisfy, one entry per obligation.
 *
 * Coverage is checked PER VERSION, never over a product space: the version axis is a selector and
 * every version is reachable by construction, so the existing check runs unchanged once per
 * version and keeps its exact gap reporting. A version with branches has an obligation; a version
 * without branches does not.
 *
 * Why this is shared rather than written into each block's issue function: validation reads
 * version-resolved data, so a banked block was only ever checked for the version on screen — a
 * coverage gap authored in version 3 stayed invisible until someone happened to step to it. Seven
 * blocks branch, and seven copies of "remember to loop the versions" is six chances to forget.
 *
 * `versionIndex` is 1-based for display and `null` for a block with no bank, which is what lets a
 * caller label the message only where a label means something. `scenarioIndex` is the same idea
 * for the third place a branch set can live.
 *
 * THE THIRD PLACE. A scenario ROW may carry its own equation and branches — see
 * `rolesForScenarioRow` — and this function predates that, so a follower's nested branching was
 * resolved, failed closed on a gap, and reported nothing. The owner found it the way you would
 * expect: "the nested branching is not giving me warnings about coverage gaps."
 *
 * It is the identical failure the version loop above exists to prevent, one axis over: a gap
 * nobody is told about, in a set nobody is looking at. Fixed here rather than in MCQ because six
 * blocks call this, and a seventh copy of "remember to loop the rows" is six chances to forget.
 *
 * The three cases are mutually exclusive and need no product: a block that FOLLOWS cannot also
 * hold a bank (§2.2), and a block with rows does not consult its own `conditionalBranches`.
 */
export function branchSetsToCheck(data) {
    if (data.scenarioBranches && data.scenarioBranches.length > 0) {
        /* One obligation per row THAT BRANCHES. A row without nested branches answers with its own
           roles and has nothing to cover, exactly as a version without branches does. */
        return data.scenarioBranches.map((row, i) => ({
            versionIndex: null,
            scenarioIndex: i + 1,
            equation: row.conditionalEquation,
            branches: row.conditionalBranches,
        }));
    }
    if (!data.versions || data.versions.length === 0) {
        return [{ versionIndex: null, scenarioIndex: null, equation: data.conditionalEquation, branches: data.conditionalBranches }];
    }
    return data.versions.map((v, i) => ({
        versionIndex: i + 1,
        scenarioIndex: null,
        equation: v.conditionalEquation,
        branches: v.conditionalBranches,
    }));
}
/** The fields this block is currently holding — the key a version stores. */
export function fieldVersionKey(data, keys) {
    return Object.fromEntries(keys.filter(k => k in data).map(k => [k, data[k]]));
}
/**
 * The head a NON-BRANCHING block carries: its question, and its feedback where it has one.
 *
 * Deliberately NOT `applyVersionHead`. That one also carries the branch set and its retained
 * draft, which these four blocks do not have — and adding three keys the block never had made the
 * editor's derived shape differ from its own state, which `useRestoredData`'s field-set guard
 * correctly refused the moment a bank was created. Numerical went further: its feedback lives
 * outside its data entirely, so even `feedback` must not be invented.
 */
function applyFieldHead(data, version) {
    const out = { ...data };
    out.question = version.question;
    if ('feedback' in out)
        out.feedback = version.feedback;
    return out;
}
export function applyFieldVersion(data, versionId, keys) {
    const version = pickVersion(data.versions, versionId);
    if (!version)
        return data;
    const out = { ...applyFieldHead(data, version) };
    // Only what the version actually carries: a key added to the list after a version was authored
    // must leave that version's blocks alone rather than blanking the field.
    for (const k of keys)
        if (k in version)
            out[k] = version[k];
    return out;
}
export function writeFieldVersion(current, versionId, next, keys) {
    const version = pickVersion(current.versions, versionId);
    // The same hole as `writeVersionWith`'s, one funnel over — see `restateLinkFields`.
    if (!version)
        return restateLinkFields(current, next);
    const nextRec = next;
    const currentRec = current;
    // Mirror of `applyFieldHead`: only the head these blocks actually have moves, so a
    // non-branching block never gains an empty branch set and numerical never gains a `feedback`
    // key that would overwrite the one it keeps elsewhere.
    const edited = { ...markVersionReviewed(version), question: nextRec.question };
    if ('feedback' in nextRec)
        edited.feedback = nextRec.feedback;
    Object.assign(edited, fieldVersionKey(nextRec, keys));
    const out = { ...nextRec, question: currentRec.question };
    if ('feedback' in currentRec)
        out.feedback = currentRec.feedback;
    /* THE SECOND FUNNEL, and it needs the same guard — see `writeVersionWith`. The value blocks
       come through here and the choice blocks through there, so a rule applied to one of them is
       a rule half the editor follows. Numerical, which is where this was reported, is one of
       these. */
    Object.assign(out, pick(current, LINK_FIELDS));
    out.versions = (current.versions ?? []).map(v => (v.id === version.id ? edited : v));
    return out;
}
export function addFieldVersion(data, fromVersionId, newId, keys) {
    return addVersionWith(data, fromVersionId, newId, fieldVersionKey(data, keys));
}
export function removeFieldVersion(data, versionId, keys) {
    return removeVersionWith(data, versionId, (d, id) => applyFieldVersion(d, id, keys));
}
//# sourceMappingURL=versions.js.map